import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Button,
  InputNumber,
  notification,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FC, ReactNode, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SystemLoadView } from 'protocol/api/core/svc_adminka_pb';
import { LoadLevel } from 'protocol/model/dto_system_load_pb';

import { useAppDispatch } from '@/shared/hooks';

import { userModel } from '@/entities/user';

import {
  columnTooltips,
  emptySettings,
  levelLabels,
  levelTagColor,
  SourceKey,
  sourceKeys,
  sourceLabels,
  SourceSettings,
  SystemLoadSettings,
} from './config';
import { settingsFromView, thresholdsFromSettings } from './lib';
import styles from './SystemLoadTab.module.scss';

export const SystemLoadTab: FC = () => {
  const { formatMessage, formatDate, formatTime } = useIntl();
  const dispatch = useAppDispatch();

  const [view, setView] = useState<SystemLoadView | undefined>();
  const [settings, setSettings] = useState<SystemLoadSettings>(emptySettings);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const applyViewWithSettings = (nextView: SystemLoadView | undefined) => {
    if (!nextView) { return; }

    setView(nextView);
    setSettings(settingsFromView(nextView));
  };

  const loadSystemLoad = async () => {
    try {
      setIsLoading(true);

      const response = await dispatch(
        userModel.thunks.getSystemLoad({}),
      ).unwrap();

      applyViewWithSettings(response.SystemLoad);
    } catch (error) {
      notification.error({ message: error?.message });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSystemLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSource = (
    sourceKey: SourceKey,
    patch: Partial<SourceSettings>,
  ) => {
    setSettings((currentSettings) => ({
      ...currentSettings,
      [sourceKey]: { ...currentSettings[sourceKey], ...patch },
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const response = await dispatch(
        userModel.thunks.setSystemLoadSettings({
          ManualUploadKafka: settings.UploadKafka.manual,
          ManualUploadCBCT: settings.UploadCBCT.manual,
          ManualPipelineOrgPending: settings.PipelineOrgPending.manual,
          ManualPipelineOrgsCount: settings.PipelineOrgsCount.manual,
          UploadKafkaEnabled: settings.UploadKafka.enabled,
          UploadCBCTEnabled: settings.UploadCBCT.enabled,
          PipelineOrgPendingEnabled: settings.PipelineOrgPending.enabled,
          PipelineOrgsCountEnabled: settings.PipelineOrgsCount.enabled,
          UploadKafkaThresholds: thresholdsFromSettings(settings.UploadKafka),
          UploadCBCTThresholds: thresholdsFromSettings(settings.UploadCBCT),
          PipelineOrgPendingThresholds: thresholdsFromSettings(
            settings.PipelineOrgPending,
          ),
          PipelineOrgsCountThresholds: thresholdsFromSettings(
            settings.PipelineOrgsCount,
          ),
        }),
      ).unwrap();

      applyViewWithSettings(response.SystemLoad);

      notification.success({
        message: formatMessage({
          id: 'systemLoad.saved',
          defaultMessage: 'System load settings saved',
        }),
      });
    } catch (error) {
      notification.error({ message: error?.message });
    } finally {
      setIsSaving(false);
    }
  };

  const hasInvalidThresholds = sourceKeys.some(
    (sourceKey) =>
      settings[sourceKey].medium < 0 ||
      settings[sourceKey].medium > settings[sourceKey].high,
  );

  const notApplicable = (
    <Typography.Text type="secondary">
      <FormattedMessage id="systemLoad.notApplicable" defaultMessage="N/A" />
    </Typography.Text>
  );

  // per-report metrics carry no global level - render N/A instead of a tag
  const renderLevelTag = (level: LoadLevel) => {
    if (level === LoadLevel.LoadLevel_InvalidValue) {
      return notApplicable;
    }

    return (
      <Tag color={levelTagColor[level]}>
        {formatMessage(levelLabels[level])}
      </Tag>
    );
  };

  const renderColumnTitle = (title: ReactNode, tooltip: string) => (
    <Space size={4}>
      {title}
      <Tooltip title={tooltip}>
        <InfoCircleOutlined rev={undefined} className={styles.columnHint} />
      </Tooltip>
    </Space>
  );

  const columns: ColumnsType<SourceKey> = [
    {
      title: renderColumnTitle(
        <FormattedMessage id="systemLoad.column.source" defaultMessage="Source" />,
        formatMessage(columnTooltips.source),
      ),
      key: 'source',
      render: (_, sourceKey) => formatMessage(sourceLabels[sourceKey]),
    },
    {
      title: renderColumnTitle(
        <FormattedMessage
          id="systemLoad.column.enabled"
          defaultMessage="Enabled"
        />,
        formatMessage(columnTooltips.enabled),
      ),
      key: 'enabled',
      render: (_, sourceKey) => (
        <Switch
          checked={settings[sourceKey].enabled}
          onChange={(enabled) => updateSource(sourceKey, { enabled })}
        />
      ),
    },
    {
      title: renderColumnTitle(
        <FormattedMessage
          id="systemLoad.column.rawValue"
          defaultMessage="Raw value"
        />,
        formatMessage(columnTooltips.rawValue),
      ),
      key: 'rawValue',
      render: (_, sourceKey) => {
        const rawValue = view?.[sourceKey]?.RawValue;

        return rawValue === undefined ? notApplicable : Number(rawValue);
      },
    },
    {
      title: renderColumnTitle(
        <FormattedMessage
          id="systemLoad.column.computed"
          defaultMessage="Computed"
        />,
        formatMessage(columnTooltips.computed),
      ),
      key: 'computed',
      render: (_, sourceKey) =>
        renderLevelTag(
          view?.[sourceKey]?.ComputedLevel ?? LoadLevel.LoadLevel_InvalidValue,
        ),
    },
    {
      title: renderColumnTitle(
        <FormattedMessage
          id="systemLoad.column.effective"
          defaultMessage="Effective"
        />,
        formatMessage(columnTooltips.effective),
      ),
      key: 'effective',
      render: (_, sourceKey) =>
        renderLevelTag(
          view?.[sourceKey]?.EffectiveLevel ?? LoadLevel.LoadLevel_InvalidValue,
        ),
    },
    {
      title: renderColumnTitle(
        <FormattedMessage
          id="systemLoad.column.manual"
          defaultMessage="Manual override"
        />,
        formatMessage(columnTooltips.manual),
      ),
      key: 'manual',
      render: (_, sourceKey) => (
        <Select<LoadLevel>
          className={styles.manualSelect}
          value={settings[sourceKey].manual}
          onChange={(manual) => updateSource(sourceKey, { manual })}
          options={[
            LoadLevel.LoadLevel_InvalidValue,
            LoadLevel.LoadLevel_Low,
            LoadLevel.LoadLevel_Medium,
            LoadLevel.LoadLevel_High,
          ].map((level) => ({
            value: level,
            label: formatMessage(levelLabels[level]),
          }))}
        />
      ),
    },
    {
      title: renderColumnTitle(
        <FormattedMessage
          id="systemLoad.column.thresholds"
          defaultMessage="Thresholds (medium / high)"
        />,
        formatMessage(columnTooltips.thresholds),
      ),
      key: 'thresholds',
      render: (_, sourceKey) => (
        <Space>
          <InputNumber
            min={0}
            precision={0}
            value={settings[sourceKey].medium}
            onChange={(medium) =>
              updateSource(sourceKey, { medium: medium ?? 0 })
            }
          />
          <InputNumber
            min={0}
            precision={0}
            value={settings[sourceKey].high}
            onChange={(high) => updateSource(sourceKey, { high: high ?? 0 })}
          />
        </Space>
      ),
    },
  ];

  if (isLoading && !view) {
    return <Skeleton active />;
  }

  const computedAt = view?.ComputedAt?.toDate();

  return (
    <div className={styles.container}>
      <Table<SourceKey>
        columns={columns}
        dataSource={sourceKeys}
        rowKey={(sourceKey) => sourceKey}
        pagination={false}
        loading={isLoading}
      />

      <div className={styles.footer}>
        <Typography.Text type="secondary">
          {computedAt ? (
            <FormattedMessage
              id="systemLoad.computedAt"
              defaultMessage="Raw upload metrics updated: {dateTime}"
              values={{
                dateTime: `${formatDate(computedAt)} ${formatTime(computedAt)}`,
              }}
            />
          ) : (
            <FormattedMessage
              id="systemLoad.neverComputed"
              defaultMessage="Raw upload metrics were not collected yet"
            />
          )}
        </Typography.Text>

        <Space>
          <Button onClick={loadSystemLoad} disabled={isLoading || isSaving}>
            <FormattedMessage id="systemLoad.refresh" defaultMessage="Refresh" />
          </Button>

          <Button
            type="primary"
            onClick={handleSave}
            loading={isSaving}
            disabled={hasInvalidThresholds}
          >
            <FormattedMessage id="systemLoad.save" defaultMessage="Save" />
          </Button>
        </Space>
      </div>
    </div>
  );
};
